import Link from "next/link";
import { useRouter } from "next/router";
import { FC, useMemo } from "react";

import {
  isStatusFilter,
  statusMatchesFilter,
  type StatusFilter,
} from "@src/features/guestFeedback/GuestFeedbackView";
import { useGuestFeedbackRecommendationsQuery } from "@src/features/guestFeedback/GuestFeedbackView.generated";
import { getMockRecommendations } from "@src/features/guestFeedback/recommendationsMockData";
import { useRecommendationsMockData } from "@src/features/recommendations/RecommendationsMockDataContext";
import { GuestFeedbackCorrectiveActionStatus } from "@src/types.generated";
import CircleCheckmarkOutlineIcon from "@src/lib/icons/18px/circle-checkmark-outline";
import EmojiOutlineIcon from "@src/lib/icons/18px/emoji-outline";
import EyeClosedIcon from "@src/lib/icons/18px/eye-closed";
import GridLayoutCols3Icon from "@src/lib/icons/18px/grid-layout-cols-3";
import InboxIcon from "@src/lib/icons/18px/inbox";
import LineChartIcon from "@src/lib/icons/18px/line-chart";
import ProgressIndicator2FillIcon from "@src/lib/icons/18px/progress-indicator-2-fill";
import KnowledgeCheckIcon from "@src/lib/icons/18px/knowledge-check";
import OpieOutlineIcon from "@src/lib/icons/18px/opie-outline";
import StarOutlineIcon from "@src/lib/icons/18px/star-outline";
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@src/lib/ui/Sidebar";
import { cn } from "@src/lib/utils/cn";

const SECONDARY_NAV_MENU_BUTTON_CLASS = "text-gray-11 [&>svg]:size-4";

type ComingSoonSignalItem = {
  label: string;
  Icon: FC<{ className?: string }>;
};

const COMING_SOON_SIGNAL_ITEMS: ComingSoonSignalItem[] = [
  { label: "Ask Opus", Icon: OpieOutlineIcon },
  { label: "Knowledge Check", Icon: KnowledgeCheckIcon },
  { label: "Course feedback", Icon: StarOutlineIcon },
];

type RecommendationStatusNavItem = {
  value: StatusFilter;
  label: string;
  Icon: FC<{ className?: string }>;
};

const RECOMMENDATION_STATUS_NAV_ITEMS: RecommendationStatusNavItem[] = [
  { value: "all", label: "All", Icon: GridLayoutCols3Icon },
  { value: "pending", label: "Inbox", Icon: InboxIcon },
  {
    value: "inProgress",
    label: "In progress",
    Icon: ProgressIndicator2FillIcon,
  },
  { value: "measuring", label: "Measuring impact", Icon: LineChartIcon },
  { value: "completed", label: "Done", Icon: CircleCheckmarkOutlineIcon },
  { value: "dismissed", label: "Dismissed", Icon: EyeClosedIcon },
];

const getRecommendationStatusHref = (
  value: StatusFilter,
): React.ComponentProps<typeof Link>["href"] => {
  if (value === "all") return "/recommendations";
  return { pathname: "/recommendations", query: { status: value } };
};

const getActiveRecommendationStatus = (
  status: string | string[] | undefined,
): StatusFilter => {
  if (typeof status === "string" && isStatusFilter(status)) return status;
  return "all";
};

const NavCount: FC<{ count?: number }> = ({ count }) => {
  if (count === undefined) return null;
  return (
    <span className="ml-auto text-xs text-gray-8 tabular-nums">{count}</span>
  );
};

const RecommendationsSecondaryNavSection: FC = () => {
  const { pathname, query } = useRouter();
  const { useMockData } = useRecommendationsMockData();
  const { data, loading: queryLoading } = useGuestFeedbackRecommendationsQuery({
    variables: { input: {} },
    skip: useMockData,
  });

  const loading = queryLoading && !useMockData;
  const recommendations = useMemo(
    () =>
      useMockData
        ? getMockRecommendations()
        : data?.GuestFeedbackRecommendations ?? [],
    [data, useMockData],
  );

  const isGuestFeedbackActive = pathname === "/guest-feedback";
  const isRecommendationsRoute = pathname === "/recommendations";
  const activeStatus = getActiveRecommendationStatus(query.status);

  const statusCounts: Record<StatusFilter, number> = useMemo(
    () => ({
      all: recommendations.filter((rec) =>
        statusMatchesFilter(rec.status, "all"),
      ).length,
      pending: recommendations.filter(
        (rec) => rec.status === GuestFeedbackCorrectiveActionStatus.Pending,
      ).length,
      inProgress: recommendations.filter(
        (rec) => rec.status === GuestFeedbackCorrectiveActionStatus.InProgress,
      ).length,
      measuring: recommendations.filter(
        (rec) =>
          rec.status === GuestFeedbackCorrectiveActionStatus.MeasuringImpact,
      ).length,
      completed: recommendations.filter(
        (rec) => rec.status === GuestFeedbackCorrectiveActionStatus.Completed,
      ).length,
      dismissed: recommendations.filter(
        (rec) => rec.status === GuestFeedbackCorrectiveActionStatus.Rejected,
      ).length,
    }),
    [recommendations],
  );

  return (
    <SidebarGroup>
      <SidebarGroupContent>
        <div className="mb-1 px-2 text-xs font-medium text-gray-8">
          Recommendations
        </div>
        <SidebarMenu>
          {RECOMMENDATION_STATUS_NAV_ITEMS.map((item) => {
            const Icon = item.Icon;
            const active =
              isRecommendationsRoute && activeStatus === item.value;

            return (
              <SidebarMenuItem key={item.value}>
                <SidebarMenuButton
                  asChild
                  isActive={active}
                  aria-current={active ? "page" : undefined}
                  className={cn(
                    SECONDARY_NAV_MENU_BUTTON_CLASS,
                    active && "[&_span:last-child]:text-gray-12",
                  )}
                >
                  <Link href={getRecommendationStatusHref(item.value)}>
                    <Icon />
                    <span>{item.label}</span>
                    <NavCount
                      count={loading ? undefined : statusCounts[item.value]}
                    />
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            );
          })}
        </SidebarMenu>

        <div className="mt-4 mb-1 px-2 text-xs font-medium text-gray-8">
          Signals
        </div>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              asChild
              isActive={isGuestFeedbackActive}
              aria-current={isGuestFeedbackActive ? "page" : undefined}
              className={SECONDARY_NAV_MENU_BUTTON_CLASS}
            >
              <Link href="/guest-feedback">
                <EmojiOutlineIcon />
                <span>Guest feedback</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>

        <div className="mt-4 mb-1 px-2 text-xs font-medium text-blue-8">
          More signals coming soon
        </div>
        <SidebarMenu>
          {COMING_SOON_SIGNAL_ITEMS.map((item) => {
            const Icon = item.Icon;

            return (
              <SidebarMenuItem key={item.label}>
                <SidebarMenuButton
                  disabled
                  className={cn(
                    SECONDARY_NAV_MENU_BUTTON_CLASS,
                    "cursor-not-allowed hover:bg-transparent",
                  )}
                >
                  <Icon />
                  <span>{item.label}</span>
                </SidebarMenuButton>
              </SidebarMenuItem>
            );
          })}
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  );
};

export default RecommendationsSecondaryNavSection;
